"use client";
import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  OrganizationSummary,
  Prompt,
  Tag,
  MessageRole,
} from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import { authClient } from "@constructr-sdk/auth/client";
import type {
  UsePublicPromptDetailsParams,
  UsePublicPromptDetailsReturn,
} from "~/modules/main/hooks/use-public-prompt-details/use-public-prompt-details.interface";

// Global state atoms
const promptAtom = atom<Prompt | null>(null);
const shareIdAtom = atom<string | null>(null);
const parsedVariablesAtom = atom<{ name: string; type: "string" }[]>([]);
const variablesByMessageIdAtom = atom<Record<string, string[]>>({});
const tagsDictionaryAtom = atom<Record<string, Tag> | undefined>(undefined);
const isAuthenticatedAtom = atom<boolean>(false);
const organizationsAtom = atom<OrganizationSummary[]>([]);
const loadErrorAtom = atom<string | null>(null);

/**
 * Helper function to extract dynamic variables from prompt content.
 * Variables are in the format {{variable_name}}.
 */
const extractVariables = (content: string): string[] => {
  const regex = /\{\{([^}]+)\}\}/g;
  const matches: string[] = [];
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (match[1]) {
      matches.push(match[1].trim());
    }
  }
  return matches;
};

/**
 * Parse all variables from prompt messages and build the variables map.
 */
const parsePromptVariables = (
  prompt: Prompt | null,
): {
  parsedVariables: { name: string; type: "string" }[];
  variablesByMessageId: Record<string, string[]>;
} => {
  if (!prompt) {
    return { parsedVariables: [], variablesByMessageId: {} };
  }
  const allVariables = new Set<string>();
  const variablesByMessageId: Record<string, string[]> = {};

  prompt.messages.forEach((message) => {
    const vars = extractVariables(message.content);
    if (vars.length > 0) {
      variablesByMessageId[message.id] = vars;
      vars.forEach((v) => allVariables.add(v));
    }
  });

  const parsedVariables = Array.from(allVariables).map((name) => ({
    name,
    type: "string" as const,
  }));

  return { parsedVariables, variablesByMessageId };
};

export function usePublicPromptDetails(
  params: UsePublicPromptDetailsParams,
): UsePublicPromptDetailsReturn {
  // Global state
  const [prompt, setPrompt] = useAtom(promptAtom);
  const [shareId, setShareId] = useAtom(shareIdAtom);
  const [parsedVariables, setParsedVariables] = useAtom(parsedVariablesAtom);
  const [variablesByMessageId, setVariablesByMessageId] = useAtom(
    variablesByMessageIdAtom,
  );
  const [tagsDictionary, setTagsDictionary] = useAtom(tagsDictionaryAtom);
  const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
  const [organizations, setOrganizations] = useAtom(organizationsAtom);
  const [loadError, setLoadError] = useAtom(loadErrorAtom);

  // Active organization detection for authenticated users
  const { data: activeOrg } = authClient.useActiveOrganization();
  const didSetActiveRef = useRef<boolean>(false);

  // TRPC queries
  const {
    data: promptData,
    isLoading: isLoadingPrompt,
    isError: isPromptError,
    error: promptError,
  } = trpcReactQueryApi.main.publicPrompts.byShareId.useQuery(
    { shareId: shareId ?? "" },
    { enabled: !!shareId },
  );

  const {
    data: tagsData,
    isLoading: isLoadingTags,
  } = trpcReactQueryApi.main.publicPrompts.tagsByShareId.useQuery(
    { shareId: shareId ?? "" },
    { enabled: !!shareId },
  );

  // Authentication check
  const { data: sessionData } = authClient.useSession();

  // Organizations query (only if authenticated)
  const { data: orgsData } = authClient.useListOrganizations();

  // Copy prompt mutation
  const copyPromptMutation = trpcReactQueryApi.main.prompts.create.useMutation({
    onSuccess: () => {
      toast.success("Prompt copied to your library");
    },
    onError: (error) => {
      toast.error(`Failed to copy prompt: ${error.message}`);
    },
  });

  // Update authentication status
  useEffect(() => {
    setIsAuthenticated(!!sessionData?.user);
  }, [sessionData, setIsAuthenticated]);

  // Update organizations
  useEffect(() => {
    if (orgsData) {
      const orgSummaries: OrganizationSummary[] = orgsData.map((org: {
        id: string; name: string; slug: string; createdAt: Date; logo?: string | null; metadata?: any;
      }) => ({
        id: org.id,
        name: org.name,
        slug: org.slug ?? null,
        imageUrl: org.logo ?? null,
        createdAt: org.createdAt ?? null,
        updatedAt: null,
      }));
      setOrganizations(orgSummaries);
    } else {
      setOrganizations([]);
    }
  }, [orgsData, setOrganizations]);

  // Bootstrap active organization for authenticated users to prevent 401 errors
  // This ensures ctx.session.activeOrganizationId is available for AI Assistant and other protected calls
  useEffect(() => {
    if (didSetActiveRef.current) return;
    if (isAuthenticated && !activeOrg?.id && (organizations?.length ?? 0) > 0) {
      didSetActiveRef.current = true;
      void authClient.organization.setActive({ organizationId: organizations[0]!.id });
    }
  }, [isAuthenticated, activeOrg?.id, organizations]);

  // Update prompt and derived state
  useEffect(() => {
    if (promptData) {
      setPrompt(promptData);
      const { parsedVariables: vars, variablesByMessageId: varsByMsg } =
        parsePromptVariables(promptData);
      setParsedVariables(vars);
      setVariablesByMessageId(varsByMsg);
      setLoadError(null);
    }
  }, [
    promptData,
    setPrompt,
    setParsedVariables,
    setVariablesByMessageId,
    setLoadError,
  ]);

  // Update tags dictionary
  useEffect(() => {
    if (tagsData) {
      setTagsDictionary(tagsData);
    }
  }, [tagsData, setTagsDictionary]);

  // Handle errors
  useEffect(() => {
    if (isPromptError && promptError) {
      setLoadError(promptError.message ?? "Failed to load prompt");
      setPrompt(null);
    }
  }, [isPromptError, promptError, setLoadError, setPrompt]);

  // Initialize command
  const initialize = useCallback(
    async (newShareId: string) => {
      setShareId(newShareId);
      setLoadError(null);
    },
    [setShareId, setLoadError],
  );

  // Copy prompt to library command
  const copyPromptToLibrary = useCallback(
    async (destinationOrganizationId: string) => {
      if (!isAuthenticated) {
        throw new Error("You must be logged in to copy prompts to your library");
      }
      if (!prompt) {
        throw new Error("No prompt available to copy");
      }
      if (!destinationOrganizationId) {
        throw new Error("Destination organization is required");
      }

      // Validate that the user belongs to the destination organization
      const belongsToOrg = organizations.some(
        (org) => org.id === destinationOrganizationId,
      );
      if (!belongsToOrg) {
        throw new Error("Invalid destination organization");
      }

      // Create a new private copy of the prompt
      const result = await copyPromptMutation.mutateAsync({
        title: prompt.title,
        messages: prompt.messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
      });

      return { newPromptId: result.id };
    },
    [isAuthenticated, prompt, organizations, copyPromptMutation],
  );

  // Build concatenated prompt command
  const buildConcatenatedPrompt = useCallback(() => {
    if (!prompt) {
      return "";
    }

    const roleLabels: Record<MessageRole, string> = {
      [MessageRole.SYSTEM]: "SYSTEM",
      [MessageRole.USER]: "USER",
      [MessageRole.ASSISTANT]: "ASSISTANT",
    };

    const parts = prompt.messages.map((msg) => {
      const roleLabel = roleLabels[msg.role] ?? msg.role.toUpperCase();
      return `${roleLabel}:\n${msg.content}`;
    });

    return parts.join("\n\n");
  }, [prompt]);

  const isLoading = isLoadingPrompt || isLoadingTags;

  return {
    // State
    prompt,
    shareId,
    parsedVariables,
    variablesByMessageId,
    tagsDictionary,
    isAuthenticated,
    organizations,
    isLoading,
    loadError,

    // Commands
    initialize,
    copyPromptToLibrary,
    buildConcatenatedPrompt,
  };
}